import { useEffect, useState } from "react";

export type AuditGenerationMode = "running" | "complete";

const GENERATION_STEPS = [
  "Checking your public pages and technical signals…",
  "Preparing your high-level AI Visibility Score…",
  "Generating your client-safe PDF report…",
];

interface AuditGenerationFeedbackProps {
  mode: AuditGenerationMode;
  /** Copy only: preserve the three-step timer and delivery predicates. */
  pdfStepCopy?: string;
  email?: string;
  pdfReady?: boolean;
  emailDelivered?: boolean;
  className?: string;
}

/**
 * Gives both Free AI Visibility Score journeys the same truthful, accessible
 * feedback while the worker creates the score and PDF. It never exposes
 * owner-only findings or claims email delivery before the worker confirms it.
 */
export default function AuditGenerationFeedback({
  mode,
  pdfStepCopy,
  email,
  pdfReady = false,
  emailDelivered = false,
  className = "",
}: AuditGenerationFeedbackProps) {
  const [activeStep, setActiveStep] = useState(0);

  useEffect(() => {
    if (mode !== "running") {
      setActiveStep(0);
      return;
    }

    setActiveStep(0);
    const timer = window.setInterval(() => {
      setActiveStep((current) => Math.min(current + 1, GENERATION_STEPS.length - 1));
    }, 2400);
    return () => window.clearInterval(timer);
  }, [mode]);

  if (mode === "complete") {
    const message = pdfReady && emailDelivered
      ? `Your score and PDF report are ready. A copy has also been sent to ${email ?? "your inbox"}.`
      : pdfReady
        ? "Your score and PDF report are ready to view. We are still finalising the email delivery."
        : emailDelivered
          ? `Your score is ready and the PDF report has been sent to ${email ?? "your inbox"}.`
          : "Your score is ready. The PDF report is still being finalised.";

    return (
      <div
        className={`flex items-start gap-3 rounded-xl border px-4 py-3 ${className}`}
        role="status"
        aria-live="polite"
        aria-atomic="true"
        style={{ background: "#00C9A711", borderColor: "#00C9A744", color: "#D7FFF6" }}
      >
        <span
          className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full"
          aria-hidden="true"
          style={{ background: "#00C9A722", color: "#00C9A7" }}
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
            <path d="m5 12 4 4L19 6" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </span>
        <div>
          <p className="text-sm font-bold" style={{ color: "#00C9A7" }}>Score generation complete</p>
          <p className="mt-0.5 text-xs leading-relaxed" style={{ color: "#B9CEC9" }}>{message}</p>
        </div>
      </div>
    );
  }

  return (
    <div
      className={`text-center ${className}`}
      role="status"
      aria-live="polite"
      aria-atomic="true"
      aria-busy="true"
    >
      <div
        className="mx-auto mb-5 flex h-16 w-16 items-center justify-center rounded-full"
        style={{ background: "#FF8C0022", color: "#FF8C00" }}
      >
        <svg className="h-8 w-8 animate-spin" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" aria-hidden="true">
          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 0 1 8-8V0C5.373 0 0 5.373 0 12h4z" />
        </svg>
      </div>
      <p className="text-xl font-bold" style={{ color: "#F7F5F2" }}>Preparing your score and PDF report</p>
      <p className="mt-2 text-sm" style={{ color: "#8899A6" }}>{activeStep === 2 ? pdfStepCopy ?? GENERATION_STEPS[activeStep] : GENERATION_STEPS[activeStep]}</p>
      <div className="mx-auto mt-6 flex max-w-sm gap-2" aria-hidden="true">
        {GENERATION_STEPS.map((step, index) => (
          <span
            key={step}
            className="h-1 flex-1 rounded-full transition-colors duration-300"
            style={{ background: index <= activeStep ? "#FF8C00" : "#2A3A4A" }}
          />
        ))}
      </div>
      <p className="mt-4 text-xs" style={{ color: "#556F85" }}>This usually takes under a minute. Keep this page open while we prepare your result.</p>
    </div>
  );
}
